import { omit } from 'lodash';

import { createReducer } from 'utils/redux';
import { RESET_ALL as RESET_ON_LOGOUT, danger } from 'redux-store/reducers/common-reducer';
import { hideLoading, showLoading } from 'redux-store/reducers/loading-reducer';

import {
  addDeliverableEntry,
  getReportDeliverablesResponse, setActiveDeliverable
} from 'modules/campaigns/components/campaign-report/metrics/reducers/campaign-report-metrics';

import { API, post as postRequest } from 'utils/api';
import {
  DELIVERABLE_TYPE_INSTAGRAM_POST,
  DELIVERABLE_TYPE_INSTAGRAM_REEL,
  DELIVERABLE_TYPE_INSTAGRAM_REGRAM_POST,
  DELIVERABLE_TYPE_TIKTOK_VIDEO,
  generateNewDeliverableId,
  getAdditionalDeliverablePossibleTaskTypes,
  isRegramContent
} from 'utils/deliverables';
import { __t, applyTranslations } from 'utils/language';
import { getNewAnalytic } from 'utils/analytics';

import type {
  Action,
  AdminDeliverable,
  AdminInfluencerPreview,
  DeliverableAttachmentType,
  DeliverableType,
  Dispatch,
  GetState,
  ThunkAction
} from 'types';

type State = {
  deliverable: AdminDeliverable | undefined | null,
  uploadingIds: Array<string>
};

applyTranslations(require('./admin-additional-deliverables.i18n.json'));

const { REPORTS } = API.END_POINTS;

const DELIVERABLE_CREATED = 'adminAdditionalDeliverable/DELIVERABLE_CREATED';
const DELIVERABLE_DELETED = 'adminAdditionalDeliverable/DELIVERABLE_DELETED';

const initialState: State = {
  deliverable: null,
  uploadingIds: []
};

const getNewDeliverable = (influencer: AdminInfluencerPreview, offerId: number, isCA2: boolean, deliverableTypes: Array<DeliverableType>) => ({
  id: generateNewDeliverableId(),
  additional: true,
  deliverableType: deliverableTypes[0],
  creatorProfile: influencer,
  offerId,
  reach: 0,
  impressions: 0,
  analytics: [getNewAnalytic(deliverableTypes[0])],
  post: null,
  postAttachments: [],
  contentApprovalV2: isCA2
});

export const createDeliverableEntry = (influencer: AdminInfluencerPreview, isCA2: boolean, deliverableTypes: Array<DeliverableType>): Action => ({
  type: DELIVERABLE_CREATED,

  payload: {
    deliverable: getNewDeliverable(influencer, influencer.offerId, isCA2, deliverableTypes)
  }
});

export const deleteDeliverable = (): Action => ({
  type: DELIVERABLE_DELETED
});

export const getDeliverableAttachmentUploadingID = (
  metricsId: number,
  type: DeliverableAttachmentType
) => `${metricsId}-${type}`;

export const saveDeliverable = (campaignId: number): ThunkAction => async (dispatch: Dispatch, getState: GetState) => {
  const deliverable = getState().adminAdditionalDeliverable.deliverable;

  if (!deliverable) {
    return false;
  }

  const url = REPORTS.metrics.createAdditionalDeliverable(deliverable.offerId);
  const hasPost = [
    DELIVERABLE_TYPE_INSTAGRAM_REGRAM_POST,
    DELIVERABLE_TYPE_INSTAGRAM_POST,
    DELIVERABLE_TYPE_INSTAGRAM_REEL,
    DELIVERABLE_TYPE_TIKTOK_VIDEO
  ].includes(deliverable.deliverableType);
  const deliverableType = deliverable.deliverableType.replace('_regram', '');
  const regram = isRegramContent(deliverable.deliverableType);
  const isTikTokDeliverable = deliverableType === DELIVERABLE_TYPE_TIKTOK_VIDEO;
  const modifiedDeliverable = { ...deliverable, deliverableType, regram };

  const mappedMetrics = (deliverable.analytics || []).map(analytic => ({
    metricsData: omit(analytic, ['id', 'matchedAttachments', 'asset']),
    asset: analytic.asset
  })).filter(metric =>
    Boolean(metric.asset) // allow if has asset
    || Object.values(omit(metric.metricsData, 'metricsType')).some(Boolean) // or any non-falsy metric value
  );

  dispatch(showLoading(url));

  try {
    const responseData = await postRequest(url, {
      data: {
        // Converting to backend format: omit fields that we needed only for DeliverableTableRow rendering
        deliverable: omit(modifiedDeliverable, ['id', 'post', 'analytics', 'creatorProfile']),
        creatorId: deliverable.creatorProfile?.id,
        post: hasPost ? deliverable.post : null,
        instagramMetrics: isTikTokDeliverable ? null : mappedMetrics,
        tiktokMetrics: isTikTokDeliverable ? mappedMetrics : null
      }
    });

    const realId = responseData.data.id;

    const deliverablesResponse = await getReportDeliverablesResponse(REPORTS.metrics.getDeliverables(campaignId, ''));
    const realDeliverable = deliverablesResponse.data.find(entry => entry.id === realId);
    if (realDeliverable) {
      dispatch(addDeliverableEntry(realDeliverable));
      dispatch(setActiveDeliverable(realId));
    }

    // clear buffer after saving on backend
    dispatch(deleteDeliverable());
    dispatch(hideLoading(url));
    return true;
  } catch (error) {
    if (error.response?.status === 409 && error.response?.data?.detail?.includes('already exists')) {
      dispatch(danger(__t('admin_additional_deliverable.conflicted_post_error')));
    } else {
      dispatch(danger(__t('admin_additional_deliverable.save_deliverable_error')));
    }
    dispatch(hideLoading(url));
    throw error;
  }
};

export const createDeliverable = (influencer: AdminInfluencerPreview, campaignId: number, isCA2: boolean): ThunkAction =>
  async (dispatch, getState) => {
    const influencerCampaignDeliverables = getState().campaignDeliverables.groupedDeliverables
      .find(deliverables => deliverables.creatorProfile.id === influencer.id);
    const deliverableTypes = getAdditionalDeliverablePossibleTaskTypes(influencerCampaignDeliverables?.deliverables || []);

    await dispatch(createDeliverableEntry(influencer, isCA2, deliverableTypes));
    await dispatch(saveDeliverable(campaignId));
  };

export default createReducer<State, any>('adminAdditionalDeliverable', initialState, {
  [RESET_ON_LOGOUT]: () => initialState,
  [DELIVERABLE_DELETED]: () => initialState,
  [DELIVERABLE_CREATED]: (state, { deliverable }) => ({
    ...state,
    deliverable
  })
});
